import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronDown, ChevronUp, Play, LogOut, ChevronLeft, ChevronRight,
  Trophy, FileText, PlayCircle, ExternalLink, ClipboardList, Lock, CheckCircle2,
  Clock, BarChart3, Award, Layers, RotateCcw, Maximize, Minimize,
} from 'lucide-react'
import { gradientForCategory, formatDuration } from '../../components/ui/CourseCard'
import type { ApiCourseDetail, ApiCourseLesson } from '../../types/course'
import { getMyEnrollments } from '@/services/enrollmentService'
import { getLessonVideo, type LessonVideo } from '@/services/courseService'
import { getEnrollmentProgress, updateLessonProgress } from '@/services/lessonProgressService'
import { getCourseTestsStatus, type CourseTestsStatus } from '@/services/testService'
import TestRunner from './TestRunner'
import CourseDocuments from './CourseDocuments'

type LessonWithKey = ApiCourseLesson & { key: number }

// ── Overlay เมื่อดูบทสุดท้ายจบ ────────────────────────────────────────────────
// คอร์สที่มี Post-test: ยังไม่ถือว่าจบจนกว่าจะผ่านแบบทดสอบหลังเรียน
function CompleteOverlay({ postTestPending, onOpenPostTest, onClose }: {
  postTestPending?: boolean
  onOpenPostTest?: () => void
  onClose: () => void
}) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 backdrop-blur-[2px]">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent">
        {postTestPending ? <ClipboardList className="h-8 w-8 text-white" strokeWidth={1.5} /> : <Trophy className="h-8 w-8 text-white" strokeWidth={1.5} />}
      </div>
      <p className="mb-2 text-2xl font-semibold text-white">{postTestPending ? 'เรียนครบทุกบทแล้ว' : 'เรียนจบแล้ว'}</p>
      <p className="mb-8 max-w-xs text-center text-sm leading-relaxed text-white/50">
        {postTestPending ? 'ทำแบบทดสอบหลังเรียนให้ผ่านเกณฑ์เพื่อจบคอร์สและรับใบประกาศ' : 'คุณเรียนครบทุกบทเรียนในคอร์สนี้แล้ว'}
      </p>
      <div className="flex items-center gap-3">
        {postTestPending && onOpenPostTest && (
          <button onClick={() => { onClose(); onOpenPostTest() }} className="bg-accent px-6 py-2.5 text-sm font-medium text-white transition hover:bg-accent2" style={{ borderRadius: 4 }}>ทำแบบทดสอบหลังเรียน</button>
        )}
        <button onClick={onClose} className={postTestPending ? 'border border-white/30 px-6 py-2.5 text-sm text-white/80 transition hover:text-white' : 'bg-accent px-6 py-2.5 text-sm font-medium text-white transition hover:bg-accent2'} style={{ borderRadius: 4 }}>ปิด</button>
      </div>
    </div>
  )
}

// ── YouTube helpers ────────────────────────────────────────────────────────
function getYoutubeId(url: string | null): string | null {
  if (!url) return null
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/)
  return m ? m[1] : null
}

let ytApiPromise: Promise<any> | null = null
function loadYouTubeIframeAPI(): Promise<any> {
  const w = window as any
  if (w.YT?.Player) return Promise.resolve(w.YT)
  if (ytApiPromise) return ytApiPromise
  ytApiPromise = new Promise((resolve) => {
    const prevCallback = w.onYouTubeIframeAPIReady
    w.onYouTubeIframeAPIReady = () => {
      prevCallback?.()
      resolve(w.YT)
    }
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(tag)
    }
  })
  return ytApiPromise
}

// ── แปลงลิงก์ในเนื้อหาบทความให้กดได้ ──────────────────────────────────────────
function linkifyText(text: string) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-accent hover:underline break-all"
      >
        {part}
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

// ── Lesson Video (ขอลิงก์ดูวิดีโอแบบตรวจสิทธิ์ก่อน แล้วเลือก player) ─────────────────────
// วิดีโอบทเรียนเป็นส่วนตัว — API สาธารณะไม่ส่งลิงก์ ต้องขอที่ /lessons/:id/video (ลงทะเบียนแล้วเท่านั้น)
// FILE → LocalVideoPlayer (presigned URL อายุ 4 ชม.) · LINK (ลิงก์เก่า) → YouTube/ไฟล์ตรงตามเดิม
function LessonVideoPlayer(props: {
  lessonId: number
  lessonKey: number
  isDone: boolean
  onEnded: (action?: string) => void
  nextLesson: LessonWithKey | null
  isLastLesson: boolean
  postTestPending?: boolean
  onOpenPostTest?: () => void
}) {
  const { lessonId, lessonKey, ...playerProps } = props
  const [video, setVideo] = useState<LessonVideo | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getLessonVideo(lessonId)
      .then((v) => { if (!cancelled) setVideo(v) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'โหลดวิดีโอไม่สำเร็จ') })
    return () => { cancelled = true }
  }, [lessonId])

  if (error) {
    return (
      <div className="mb-6 flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-sm bg-surface2 text-text-muted">
        <PlayCircle className="h-10 w-10 opacity-40" />
        <p className="px-6 text-center text-sm">{error}</p>
      </div>
    )
  }
  if (!video) {
    return (
      <div className="mb-6 flex aspect-video w-full items-center justify-center rounded-sm bg-surface2 text-sm text-text-muted">กำลังโหลดวิดีโอ...</div>
    )
  }
  const youtubeId = video.kind === 'LINK' ? getYoutubeId(video.url) : null
  return youtubeId
    ? <YoutubePlayer videoId={youtubeId} lessonKey={lessonKey} {...playerProps} />
    : <LocalVideoPlayer videoUrl={video.url} lessonKey={lessonKey} {...playerProps} />
}

// ── Empty Video Placeholder ─────────────────────────────────────────────────
// แสดงเมื่อ lesson type=VIDEO แต่ยังไม่มี video_url ใน database — ไม่ใช้วิดีโอ mock แทน
function EmptyVideoPlaceholder() {
  return (
    <div className="mb-6 flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-sm bg-surface2 text-text-muted">
      <PlayCircle className="h-10 w-10 opacity-40" />
      <p className="text-sm">ยังไม่มีวิดีโอสำหรับบทเรียนนี้</p>
    </div>
  )
}

// ── Local Video Player ────────────────────────────────────────────────────────
// - progress bar คลิกย้อนกลับได้ (ถูกจำกัดไม่ให้เกินจุดที่ดูไปไกลสุด) ไม่ให้ข้ามไปข้างหน้า
// - ติดตาม progress เองผ่าน timeupdate
// - เมื่อจบ → mark done + แสดง overlay
function LocalVideoPlayer({ videoUrl, lessonKey, isDone, onEnded, nextLesson, isLastLesson, postTestPending, onOpenPostTest }: {
  videoUrl: string
  lessonKey: number
  isDone: boolean
  onEnded: (action?: string) => void
  nextLesson: LessonWithKey | null
  isLastLesson: boolean
  postTestPending?: boolean
  onOpenPostTest?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<HTMLDivElement>(null)   // ขยายเต็มจอที่กรอบนี้ (ไม่ใช่ <video>) เพื่อให้แถบควบคุมของเราและกติกาห้ามเลื่อนข้ามยังทำงาน
  const maxWatchedRef = useRef(0)   // จุดที่ดูไปไกลสุด — บทที่ยังไม่จบเลื่อนข้ามจุดนี้ไม่ได้
  const [overlayState, setOverlayState] = useState<'none' | 'nextup' | 'complete'>('none')
  const [countdown, setCountdown] = useState(5)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  // เบราว์เซอร์ที่ขยายได้เฉพาะ <video> (เช่น iPhone) จะใช้แถบควบคุมของระบบที่เลื่อนข้ามได้ — ไม่แสดงปุ่มในกรณีนั้น
  const canFullscreen = typeof document !== 'undefined' && !!document.documentElement.requestFullscreen

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === playerRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  useEffect(() => {
    setOverlayState('none')
    setCountdown(5)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    maxWatchedRef.current = 0
    if (videoRef.current) videoRef.current.load()
  }, [lessonKey, videoUrl])

  useEffect(() => {
    if (overlayState !== 'nextup') return
    setCountdown(5)
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(t); handleGoNext(); return 0 }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState])

  const handleVideoEnded = () => {
    setIsPlaying(false)
    onEnded()
    if (isLastLesson) setOverlayState('complete')
    else setOverlayState('nextup')
  }

  const handleGoNext = () => {
    setOverlayState('none')
    onEnded('go-next')
  }

  const togglePlay = () => {
    if (!videoRef.current) return
    if (videoRef.current.paused) { videoRef.current.play(); setIsPlaying(true) }
    else { videoRef.current.pause(); setIsPlaying(false) }
  }

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void playerRef.current?.requestFullscreen().catch(() => {})
  }

  const toggleMute = () => {
    if (!videoRef.current) return
    videoRef.current.muted = !muted
    setMuted(!muted)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value)
    if (videoRef.current) videoRef.current.volume = v
    setVolume(v)
    setMuted(v === 0)
  }

  // ย้อนกลับได้เสมอ แต่ข้ามไปหน้าจุดที่ดูไปไกลสุดไม่ได้ (บทที่เรียนจบแล้วเลื่อนได้อิสระ)
  const seekTo = (target: number) => {
    const v = videoRef.current
    if (!v || !duration) return
    const limit = isDone ? duration : maxWatchedRef.current
    const t = Math.max(0, Math.min(target, limit))
    v.currentTime = t
    setCurrentTime(t)
  }

  const handleSeekBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.width <= 0) return
    seekTo(((e.clientX - rect.left) / rect.width) * duration)
  }

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div ref={playerRef} className="relative mb-6 aspect-video w-full overflow-hidden rounded-sm bg-bg group">
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-contain bg-black"
        onEnded={handleVideoEnded}
        onTimeUpdate={() => {
          const v = videoRef.current
          if (!v) return
          if (!isDone && v.currentTime > maxWatchedRef.current + 1.5) { v.currentTime = maxWatchedRef.current; return }
          maxWatchedRef.current = Math.max(maxWatchedRef.current, v.currentTime)
          setCurrentTime(v.currentTime)
        }}
        onSeeking={() => {
          const v = videoRef.current
          if (v && !isDone && v.currentTime > maxWatchedRef.current + 0.5) v.currentTime = maxWatchedRef.current
        }}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={videoUrl} />
      </video>

      {overlayState === 'none' && (
        <div className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center" onClick={togglePlay}>
          {!isPlaying && (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm transition hover:bg-black/60">
              <Play className="ml-1 h-7 w-7 fill-white text-white" />
            </div>
          )}
        </div>
      )}

      {isDone && overlayState === 'none' && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1.5 bg-emerald-500 px-2.5 py-1 text-xs font-medium text-white" style={{ borderRadius: 4 }}>
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
          เรียนแล้ว
        </div>
      )}

      {overlayState === 'none' && (
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <div className="mb-1 flex h-4 w-full cursor-pointer items-center" onClick={handleSeekBarClick} title="คลิกเพื่อย้อนกลับ (ข้ามไปข้างหน้าไม่ได้)">
            <div className="relative h-1 w-full overflow-hidden bg-white/20">
              <div className="h-full bg-accent transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="text-white transition hover:text-accent">
              {isPlaying
                ? <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                : <Play className="h-5 w-5 fill-current" />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); seekTo(currentTime - 10) }} className="flex items-center gap-1 text-xs text-white/70 transition hover:text-white" title="ย้อนกลับ 10 วินาที">
              <RotateCcw className="h-4 w-4" /> 10
            </button>
            <span className="text-xs text-white/70 tabular-nums">{formatTime(currentTime)} / {formatTime(duration)}</span>
            <div className="flex-1" />
            <button onClick={toggleMute} className="text-white/70 transition hover:text-white">
              {muted || volume === 0
                ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
                : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12M9 9l-4 3H3v4h2l4 3V9z" /></svg>}
            </button>
            <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={handleVolumeChange} className="w-16 cursor-pointer accent-accent" />
            {canFullscreen && (
              <button onClick={toggleFullscreen} className="text-white/70 transition hover:text-white" title={isFullscreen ? 'ออกจากเต็มจอ' : 'ขยายเต็มจอ'}>
                {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      )}

      {overlayState === 'nextup' && nextLesson && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-sm bg-black/75 backdrop-blur-[2px]">
          <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/50">หัวข้อถัดไป</p>
          <h3 className="mb-8 max-w-md px-6 text-center text-xl font-semibold leading-snug text-white">{nextLesson.title}</h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setOverlayState('none')} className="border border-white/30 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10" style={{ borderRadius: 4 }}>
              อยู่หัวข้อนี้ก่อน
            </button>
            <button onClick={handleGoNext} className="flex items-center gap-2 bg-accent px-5 py-2.5 text-sm font-medium text-white transition hover:bg-accent2" style={{ borderRadius: 4 }}>
              <Play className="h-4 w-4 fill-white" /> เรียนเลย
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs font-medium tabular-nums">{countdown}</span>
            </button>
          </div>
        </div>
      )}

      {overlayState === 'complete' && (
        <CompleteOverlay postTestPending={postTestPending} onOpenPostTest={onOpenPostTest} onClose={() => setOverlayState('none')} />
      )}
    </div>
  )
}

// ── YouTube Player ─────────────────────────────────────────────────────────────
// ใช้ YouTube IFrame API เพื่อดักจับ "onEnded" ได้จริง (ใช้ควบคุม overlay หัวข้อถัดไป/เรียนจบ
// เหมือน LocalVideoPlayer) — ควบคุม seek ไม่ได้เหมือนวิดีโอ mp4 ในเครื่อง เพราะเป็น iframe ของ YouTube เอง
function YoutubePlayer({ videoId, lessonKey, isDone, onEnded, nextLesson, isLastLesson, postTestPending, onOpenPostTest }: {
  videoId: string
  lessonKey: number
  isDone: boolean
  onEnded: (action?: string) => void
  nextLesson: LessonWithKey | null
  isLastLesson: boolean
  postTestPending?: boolean
  onOpenPostTest?: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)
  const [overlayState, setOverlayState] = useState<'none' | 'nextup' | 'complete'>('none')
  const [countdown, setCountdown] = useState(5)
  // กันเลื่อนข้ามวิดีโอ — จำจุดที่ดูไปไกลสุด ถ้า player กระโดดไปไกลกว่านั้น (ลากแถบเวลา) ให้ดึงกลับ
  // บทที่เรียนจบแล้ว (isDone) เลื่อนได้อิสระ
  const maxWatchedRef = useRef(0)
  const isDoneRef = useRef(isDone)
  useEffect(() => { isDoneRef.current = isDone }, [isDone])

  useEffect(() => {
    maxWatchedRef.current = 0
    const timer = setInterval(() => {
      const p = playerRef.current
      if (!p?.getCurrentTime || isDoneRef.current) return
      const t = p.getCurrentTime()
      if (t > maxWatchedRef.current + 2) p.seekTo?.(maxWatchedRef.current, true)
      else maxWatchedRef.current = Math.max(maxWatchedRef.current, t)
    }, 500)
    return () => clearInterval(timer)
  }, [lessonKey, videoId])

  useEffect(() => {
    let cancelled = false
    setOverlayState('none')
    loadYouTubeIframeAPI().then((YT) => {
      if (cancelled || !containerRef.current) return
      playerRef.current?.destroy?.()
      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, disablekb: 1 },
        events: {
          onStateChange: (e: any) => {
            if (e.data === YT.PlayerState.ENDED) handleEnded()
          },
        },
      })
    })
    return () => {
      cancelled = true
      playerRef.current?.destroy?.()
      playerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonKey, videoId])

  useEffect(() => {
    if (overlayState !== 'nextup') return
    setCountdown(5)
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(t); handleGoNext(); return 0 }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState])

  const handleEnded = () => {
    onEnded()
    setOverlayState(isLastLesson ? 'complete' : 'nextup')
  }

  const handleGoNext = () => {
    setOverlayState('none')
    onEnded('go-next')
  }

  return (
    <div className="relative mb-6 aspect-video w-full overflow-hidden rounded-sm bg-black">
      <div ref={containerRef} className="absolute inset-0 h-full w-full" />

      {isDone && overlayState === 'none' && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1.5 bg-emerald-500 px-2.5 py-1 text-xs font-medium text-white" style={{ borderRadius: 4 }}>
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
          เรียนแล้ว
        </div>
      )}

      {overlayState === 'nextup' && nextLesson && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/75 backdrop-blur-[2px]">
          <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/50">หัวข้อถัดไป</p>
          <h3 className="mb-8 max-w-md px-6 text-center text-xl font-semibold leading-snug text-white">{nextLesson.title}</h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setOverlayState('none')} className="border border-white/30 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10" style={{ borderRadius: 4 }}>
              อยู่หัวข้อนี้ก่อน
            </button>
            <button onClick={handleGoNext} className="flex items-center gap-2 bg-accent px-5 py-2.5 text-sm font-medium text-white transition hover:bg-accent2" style={{ borderRadius: 4 }}>
              <Play className="h-4 w-4 fill-white" /> เรียนเลย
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs font-medium tabular-nums">{countdown}</span>
            </button>
          </div>
        </div>
      )}

      {overlayState === 'complete' && (
        <CompleteOverlay postTestPending={postTestPending} onOpenPostTest={onOpenPostTest} onClose={() => setOverlayState('none')} />
      )}
    </div>
  )
}

// ── Article Viewer ────────────────────────────────────────────────────────────
function ArticleViewer({ content, isDone, onMarkDone }: { content: string | null; isDone: boolean; onMarkDone: () => void }) {
  return (
    <div className="mb-6 border border-[#E7E5F0] bg-white p-6" style={{ borderRadius: 6 }}>
      {content ? (
        <p className="whitespace-pre-wrap text-[15px] leading-[1.8] text-[#19172A]">{linkifyText(content)}</p>
      ) : (
        <p className="text-sm text-[#69738A]">ยังไม่มีเนื้อหาบทความสำหรับบทเรียนนี้</p>
      )}
      {!isDone && (
        <button
          onClick={onMarkDone}
          className="mt-5 flex items-center gap-2 bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent2"
          style={{ borderRadius: 4 }}
        >
          ทำเครื่องหมายว่าอ่านแล้ว
        </button>
      )}
    </div>
  )
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
// ── รายการแบบทดสอบในแถบข้าง ───────────────────────────────────────────────────
function TestSidebarItem({ kicker, label, state, active, done, locked, disabled, onClick }: {
  kicker: string
  label: string
  state: string
  active: boolean
  done?: boolean
  locked?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled && locked ? 'เรียนครบทุกบทก่อนจึงจะทำได้' : undefined}
      className={`flex w-full items-start gap-2.5 border-b border-l-2 border-[#E7E5F0] px-5 py-3 text-left transition-colors ${active ? 'border-l-accent bg-[#F0EDFF]' : 'border-l-transparent hover:bg-[#F7F5FF]'} ${disabled && !done ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-accent/70">{kicker}</p>
        <p className={`text-sm leading-snug ${active ? 'font-medium text-accent' : 'text-[#19172A]'}`}>{label}</p>
        {/* แสดงสถานะเฉพาะเมื่อทำแล้ว (ทำแล้ว/ข้ามแล้ว/ผ่านแล้ว) — ข้อความอธิบายตอนยังไม่ทำเอาออก */}
        {done && <p className="mt-0.5 text-xs text-[#69738A]">{state}</p>}
      </div>
      {/* ไอคอนสถานะด้านขวาเฉพาะตอนทำแล้ว/ถูกล็อก (ไม่มีไอคอนประจำหัวข้อ) */}
      {done ? <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.75} />
        : locked ? <Lock className="mt-1 h-4 w-4 shrink-0 text-[#9691AC]" strokeWidth={1.75} /> : null}
    </button>
  )
}

function Sidebar({ course, sidebarOpen, setSidebarOpen, currentLesson, setCurrentLesson, completedLessons, testStatus, activeTest, onSelectTest, docsActive, onSelectDocs }: {
  course: ApiCourseDetail
  sidebarOpen: boolean
  setSidebarOpen: (v: boolean) => void
  currentLesson: LessonWithKey | null
  setCurrentLesson: (l: LessonWithKey | null) => void
  completedLessons: Set<number>
  testStatus: CourseTestsStatus | null
  activeTest: 'PRE' | 'POST' | null
  onSelectTest: (kind: 'PRE' | 'POST') => void
  docsActive: boolean
  onSelectDocs: () => void
}) {
  const [openSection, setOpenSection] = useState<number | null>(0)
  const totalLessons = course.sections.reduce((acc, s) => acc + s.lessons.length, 0)
  const progress = totalLessons ? Math.round((completedLessons.size / totalLessons) * 100) : 0

  // บังคับเรียนตามลำดับ — เข้าได้เฉพาะบทที่เรียนจบแล้ว กับบทแรกที่ยังไม่จบ (บทถัดไปจากที่เรียนล่าสุด) ที่เหลือล็อก
  const orderedLessons = course.sections.flatMap((s) => s.lessons)
  const firstIncompleteIdx = orderedLessons.findIndex((l) => !completedLessons.has(l.lesson_id))
  const frontierIdx = firstIncompleteIdx === -1 ? orderedLessons.length - 1 : firstIncompleteIdx
  const lessonIdx = new Map(orderedLessons.map((l, i) => [l.lesson_id, i]))
  const isLocked = (lessonId: number) => !completedLessons.has(lessonId) && (lessonIdx.get(lessonId) ?? 0) > frontierIdx

  const lessonIcon = (lesson: ApiCourseLesson, isActive: boolean, isDone: boolean, locked: boolean) => {
    if (locked) return <Lock className="h-3.5 w-3.5 text-[#9691AC]" strokeWidth={1.75} />
    if (isDone) {
      return (
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500">
          <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )
    }
    if (isActive) return <Play className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
    if (lesson.lesson_type === 'ARTICLE') return <FileText className="h-3.5 w-3.5 text-[#69738A]" strokeWidth={1.75} />
    return <Play className="h-3.5 w-3.5 text-[#69738A]" strokeWidth={1.75} />
  }

  return (
    <>
      <aside className={`flex flex-col border-r border-[#E7E5F0] bg-white transition-all duration-300 ${sidebarOpen ? 'w-[288px]' : 'w-0 overflow-hidden'}`}>
        <div className="border-b border-[#E7E5F0] p-5">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="text-[#69738A]">ความคืบหน้า</span>
            <span className="font-semibold text-accent">{progress}%</span>
          </div>
          <div className="h-1 w-full bg-[#E7E5F0]">
            <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <button
          onClick={() => setCurrentLesson(null)}
          className={`flex w-full items-center gap-2.5 border-b border-l-2 border-[#E7E5F0] px-5 py-3 text-left transition-colors ${currentLesson === null && !activeTest && !docsActive ? 'border-l-accent bg-[#F0EDFF]' : 'border-l-transparent hover:bg-[#F7F5FF]'}`}
        >
          <div>
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-accent/70">Introduction</p>
            <span className={`text-sm leading-snug ${currentLesson === null && !activeTest && !docsActive ? 'font-medium text-accent' : 'text-[#19172A]'}`}>บทนำ</span>
          </div>
        </button>

        {course.has_attachments && (
          <button
            onClick={onSelectDocs}
            className={`flex w-full items-center gap-2.5 border-b border-l-2 border-[#E7E5F0] px-5 py-3 text-left transition-colors ${docsActive ? 'border-l-accent bg-[#F0EDFF]' : 'border-l-transparent hover:bg-[#FAFAFF]'}`}
          >
            <div>
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-accent/70">Documents</p>
              <span className={`text-sm leading-snug ${docsActive ? 'font-medium text-accent' : 'text-[#19172A]'}`}>เอกสารประกอบการเรียน</span>
            </div>
          </button>
        )}

        {testStatus?.pre && (
          <TestSidebarItem
            kicker="Pre-test"
            label="แบบทดสอบก่อนเรียน"
            active={activeTest === 'PRE'}
            state={testStatus.pre.done ? (testStatus.pre.result?.status === 'SKIPPED' ? 'ข้ามแล้ว' : 'ทำแล้ว') : 'ยังไม่ได้ทำ'}
            done={testStatus.pre.done}
            onClick={() => onSelectTest('PRE')}
            disabled={testStatus.pre.done}
          />
        )}

        <div className="flex-1 overflow-y-auto">
          {course.sections.map((section, si) => {
            const sectionMins = section.lessons.reduce((a, l) => a + l.duration_minutes, 0)
            const doneInSection = section.lessons.filter((l) => completedLessons.has(l.lesson_id)).length
            const sectionDone = section.lessons.length > 0 && doneInSection === section.lessons.length
            return (
              <div key={section.section_id} className={si > 0 ? 'border-t border-[#E7E5F0]' : ''}>
                <button
                  onClick={() => setOpenSection(openSection === si ? null : si)}
                  className="flex w-full items-center gap-3 px-5 py-3.5 transition-colors hover:bg-[#F7F5FF]"
                >
                  {/* เลขบท (สี่เหลี่ยม) — เรียนจบทั้งบทเปลี่ยนเป็นเครื่องหมายถูกสีเขียว */}
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center text-xs font-bold ${sectionDone ? 'bg-emerald-500 text-white' : 'bg-accent/10 text-accent'}`}>
                    {sectionDone ? <CheckCircle2 className="h-4 w-4" strokeWidth={2.25} /> : String(si + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium leading-snug text-[#19172A]">{section.title}</p>
                    <p className="mt-0.5 text-xs text-[#69738A]">{doneInSection}/{section.lessons.length} บท · {sectionMins} นาที</p>
                  </div>
                  {openSection === si ? <ChevronUp className="h-4 w-4 shrink-0 text-[#69738A]" /> : <ChevronDown className="h-4 w-4 shrink-0 text-[#69738A]" />}
                </button>

                {openSection === si && section.lessons.map((lesson) => {
                  const isActive = currentLesson?.key === lesson.lesson_id
                  const isDone = completedLessons.has(lesson.lesson_id)
                  const locked = isLocked(lesson.lesson_id)
                  return (
                    <button
                      key={lesson.lesson_id}
                      onClick={() => setCurrentLesson({ ...lesson, key: lesson.lesson_id })}
                      disabled={locked}
                      title={locked ? 'เรียนบทก่อนหน้าให้ครบก่อนจึงจะเข้าได้' : undefined}
                      className={`flex w-full items-start gap-2.5 border-l-2 py-2.5 pl-[3.25rem] pr-4 text-left transition-colors ${locked ? 'cursor-not-allowed border-transparent opacity-60' : isActive ? 'border-accent bg-[#F0EDFF]' : 'border-transparent hover:bg-[#F7F5FF]'}`}
                    >
                      <div className="mt-0.5 shrink-0">{lessonIcon(lesson, isActive, isDone, locked)}</div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[13px] leading-snug ${isActive ? 'font-semibold text-accent' : isDone ? 'text-[#19172A]' : 'text-[#69738A]'}`}>{lesson.title}</p>
                        <p className="mt-0.5 text-[11px] text-[#9691AC]">{formatDuration(lesson.duration_minutes)}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          })}

          {testStatus?.post && (
            <div className="border-t border-[#E7E5F0]">
              <TestSidebarItem
                kicker="Post-test"
                label="แบบทดสอบหลังเรียน"
                active={activeTest === 'POST'}
                state={testStatus.post.passed ? 'ผ่านแล้ว' : testStatus.post.unlocked ? 'พร้อมทำ' : 'เรียนครบทุกบทเพื่อปลดล็อก'}
                done={testStatus.post.passed}
                locked={!testStatus.post.unlocked && !testStatus.post.passed}
                disabled={testStatus.post.passed || !testStatus.post.unlocked}
                onClick={() => onSelectTest('POST')}
              />
            </div>
          )}
        </div>

        <div className="border-t border-[#E7E5F0] p-3">
          <button onClick={() => window.history.back()} className="flex w-full items-center gap-2 px-2 py-2 text-sm text-[#69738A] transition-colors hover:text-accent">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> ออกจากห้องเรียน
          </button>
        </div>
      </aside>

      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="absolute z-20 flex h-8 w-5 items-center justify-center border border-l-0 border-[#E7E5F0] bg-white text-[#69738A] transition hover:text-accent"
        style={{ left: sidebarOpen ? '288px' : '0px', top: '50%', transform: 'translateY(-50%)', borderRadius: '0 4px 4px 0' }}
      >
        {sidebarOpen ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
    </>
  )
}

// ── Lesson Viewer ─────────────────────────────────────────────────────────────
const LEVEL_TH: Record<string, string> = { BEGINNER: 'เริ่มต้น', INTERMEDIATE: 'ระดับกลาง', ADVANCED: 'ระดับสูง' }

function parseOutcomeList(value: string | null): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string' && x.trim()) : []
  } catch {
    return []
  }
}

function LessonViewer({ course, currentLesson, completedLessons, onMarkDone, onPrev, onNext, hasPrev, hasNext, allLessons, currentIndex, postTestPending, onOpenPostTest, onStart, startLabel }: {
  onStart: () => void
  startLabel: string
  course: ApiCourseDetail
  currentLesson: LessonWithKey | null
  completedLessons: Set<number>
  onMarkDone: (key: number) => void
  onPrev: () => void
  onNext: () => void
  hasPrev: boolean
  hasNext: boolean
  allLessons: LessonWithKey[]
  currentIndex: number
  postTestPending?: boolean
  onOpenPostTest?: () => void
}) {
  const gradient = gradientForCategory(course.category_id)
  const isDone = currentLesson ? completedLessons.has(currentLesson.key) : false
  const isLastLesson = !hasNext
  const nextLesson = hasNext ? allLessons[currentIndex + 1] : null
  const [openIntroSection, setOpenIntroSection] = useState<number | null>(0)

  const handleVideoEnded = useCallback((action?: string) => {
    if (currentLesson) onMarkDone(currentLesson.key)
    if (action === 'go-next') onNext()
  }, [currentLesson, onMarkDone, onNext])

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b border-[#E7E5F0] bg-[#FCFBFF] px-6 py-3">
        <h2 className="min-w-0 truncate text-sm text-[#69738A]">{currentLesson?.title || 'บทนำ'}</h2>
        {currentLesson && (
          <span className="shrink-0 text-xs tabular-nums text-[#69738A]">
            {currentIndex + 1} / {allLessons.length}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto bg-white p-6 md:p-10">
        {currentLesson ? (
          <div className="mx-auto" style={{ maxWidth: 1040 }}>
            {currentLesson.lesson_type === 'VIDEO' && (
              currentLesson.has_video ? (
                <LessonVideoPlayer
                  key={currentLesson.lesson_id}   // เปลี่ยนบทเรียน = เริ่ม state ใหม่ (กลับไป "กำลังโหลด")
                  lessonId={currentLesson.lesson_id}
                  lessonKey={currentLesson.key}
                  isDone={isDone}
                  onEnded={handleVideoEnded}
                  nextLesson={nextLesson}
                  isLastLesson={isLastLesson}
                  postTestPending={postTestPending}
                  onOpenPostTest={onOpenPostTest}
                />
              ) : (
                <EmptyVideoPlaceholder />
              )
            )}
            {currentLesson.lesson_type === 'ARTICLE' && (
              <ArticleViewer
                content={currentLesson.article_content}
                isDone={isDone}
                onMarkDone={() => onMarkDone(currentLesson.key)}
              />
            )}
            <h1 className="mb-1.5 text-[22px] font-semibold leading-snug text-[#19172A]">{currentLesson.title}</h1>
            <p className="mb-6 text-sm text-[#69738A]">ระยะเวลา {formatDuration(currentLesson.duration_minutes)}</p>

            <div className="flex flex-wrap items-center gap-3 border-t border-[#E7E5F0] pt-5">
              {isDone && (
                <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  เรียนแล้ว
                </span>
              )}
              <div className="flex-1" />
              {hasPrev && (
                <button onClick={onPrev} className="flex items-center gap-1 border border-[#E7E5F0] px-4 py-2 text-sm text-[#69738A] transition hover:border-accent/40 hover:text-[#19172A]" style={{ borderRadius: 4 }}>
                  <ChevronLeft className="h-4 w-4" /> ก่อนหน้า
                </button>
              )}
              {hasNext && (
                <button
                  onClick={onNext}
                  disabled={!isDone}
                  title={!isDone ? 'เรียนบทนี้ให้จบก่อนจึงจะไปบทถัดไปได้' : undefined}
                  className="flex items-center gap-1 bg-accent px-4 py-2 text-sm font-medium text-white transition hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-accent"
                  style={{ borderRadius: 4 }}
                >
                  ถัดไป <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className={`relative mx-auto mb-8 w-full max-w-md overflow-hidden bg-gradient-to-br ${gradient}`} style={{ borderRadius: 6 }}>
              {course.thumbnail_url ? (
                <img src={course.thumbnail_url} alt={course.title} className="block h-auto w-full" />
              ) : (
                <div className="flex h-40 items-center justify-center bg-black/10">
                  <PlayCircle className="h-12 w-12 text-white/60" strokeWidth={1.5} />
                </div>
              )}
            </div>

            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent/70">Course Introduction</p>
            <h1 className="mb-3 text-[26px] font-semibold leading-snug text-[#19172A]">{course.title}</h1>
            <p className="mb-6 text-[15px] leading-[1.8] text-[#69738A]">{course.description}</p>

            {/* ปุ่มเริ่มเรียน/เรียนต่อ — พาไปบทที่ถึงลำดับแล้ว (บทแรกที่ยังไม่จบ) */}
            {allLessons.length > 0 && (
              <button
                onClick={onStart}
                className="mb-8 inline-flex items-center gap-2 bg-accent px-6 py-3 text-sm font-semibold text-white transition hover:bg-accent2"
                style={{ borderRadius: 4 }}
              >
                <Play className="h-4 w-4 fill-white" /> {startLabel}
              </button>
            )}

            {/* สรุปคอร์ส */}
            <div className="mb-10 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { icon: Layers, label: 'บทเรียน', value: `${allLessons.length} บท` },
                { icon: Clock, label: 'ระยะเวลารวม', value: formatDuration(course.duration_minutes) },
                { icon: BarChart3, label: 'ระดับ', value: LEVEL_TH[course.level] || course.level },
                { icon: Award, label: 'ประกาศนียบัตร', value: course.has_certificate ? 'มี เมื่อเรียนจบ' : 'ไม่มี' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="border border-[#E7E5F0] bg-[#FCFBFF] px-4 py-3.5" style={{ borderRadius: 6 }}>
                  <Icon className="mb-2 h-4 w-4 text-accent" strokeWidth={1.75} />
                  <p className="text-[11px] text-[#69738A]">{label}</p>
                  <p className="mt-0.5 text-sm font-semibold text-[#19172A]">{value}</p>
                </div>
              ))}
            </div>

            {/* สิ่งที่จะได้รับ */}
            {parseOutcomeList(course.outcomes).length > 0 && (
              <div className="mb-10">
                <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent/70">What You Will Learn</p>
                <div className="grid grid-cols-1 gap-x-8 gap-y-3 border border-[#E7E5F0] bg-[#FCFBFF] p-5 md:grid-cols-2" style={{ borderRadius: 6 }}>
                  {parseOutcomeList(course.outcomes).map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.75} />
                      <span className="text-sm leading-relaxed text-[#4A5468]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ผู้สอน */}
            {course.instructor && (
              <div className="mb-10">
                <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent/70">Instructor</p>
                <div className="flex items-center gap-4 border border-[#E7E5F0] bg-[#FCFBFF] p-4" style={{ borderRadius: 6 }}>
                  {course.instructor.avatar_url ? (
                    <img src={course.instructor.avatar_url} alt="" className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-lg font-semibold text-accent">
                      {course.instructor.first_name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#19172A]">{course.instructor.first_name} {course.instructor.last_name}</p>
                    {course.instructor.bio && <p className="mt-0.5 text-sm text-[#69738A]">{course.instructor.bio}</p>}
                  </div>
                </div>
              </div>
            )}

            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent/70">Course Content</p>
            <div className="divide-y divide-[#E7E5F0] border-t border-[#E7E5F0]">
              {course.sections.map((section, si) => {
                const isOpen = openIntroSection === si
                const sectionMins = section.lessons.reduce((a, l) => a + l.duration_minutes, 0)
                return (
                  <div key={section.section_id}>
                    <button
                      onClick={() => setOpenIntroSection(isOpen ? null : si)}
                      className="flex w-full items-center justify-between py-4 text-left transition-colors hover:bg-[#F8F7FC]"
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="text-xs font-semibold text-accent/70">{String(si + 1).padStart(2, '0')}</span>
                        <div>
                          <p className="text-sm font-medium text-[#19172A]">{section.title}</p>
                          <p className="mt-0.5 text-xs text-[#69738A]">{section.lessons.length} บทเรียน · {sectionMins} นาที</p>
                        </div>
                      </div>
                      {isOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-[#69738A]" /> : <ChevronDown className="h-4 w-4 shrink-0 text-[#69738A]" />}
                    </button>
                    {isOpen && (
                      <div className="divide-y divide-[#E7E5F0] pb-2 pl-9">
                        {section.lessons.map((lesson) => (
                          <div key={lesson.lesson_id} className="flex items-center gap-3 py-2.5 transition-colors hover:bg-[#F8F7FC]">
                            {lesson.lesson_type === 'ARTICLE' ? <FileText className="h-3.5 w-3.5 shrink-0 text-[#69738A]" strokeWidth={1.75} />
                              : <Play className="h-3.5 w-3.5 shrink-0 text-[#69738A]" strokeWidth={1.75} />}
                            <span className="flex-1 text-sm text-[#19172A]">{lesson.title}</span>
                            {lesson.is_preview && <span className="text-xs font-medium text-accent">Preview</span>}
                            <span className="text-xs text-[#69738A]">{formatDuration(lesson.duration_minutes)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── CourseRoomPage ────────────────────────────────────────────────────────────
export default function CourseRoomPage() {
  const { state } = useLocation() as { state: { course?: ApiCourseDetail } | null }
  const navigate = useNavigate()
  const course = state?.course

  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [currentLesson, setCurrentLesson] = useState<LessonWithKey | null>(null)
  const [completedLessons, setCompletedLessons] = useState<Set<number>>(new Set())
  const [enrollmentId, setEnrollmentId] = useState<number | null>(null)
  const [testStatus, setTestStatus] = useState<CourseTestsStatus | null>(null)
  const [activeTest, setActiveTest] = useState<'PRE' | 'POST' | null>(null)
  const [showDocs, setShowDocs] = useState(false)   // แท็บ "เอกสารประกอบการเรียน" (ระดับคอร์ส)
  const preAutoShown = useRef(false)
  const courseId = course?.course_id

  // ใช้สถานะแบบทดสอบที่โหลดมา + เข้าห้องเรียนครั้งแรกแล้วมี Pre-test ที่ยังไม่ได้ทำ/ข้าม → ขึ้นหน้าทำแบบทดสอบก่อน (ข้ามได้, ขึ้นครั้งเดียวต่อการเข้าห้อง)
  const applyTestStatus = useCallback((status: CourseTestsStatus | null) => {
    setTestStatus(status)
    if (status?.pre && !preAutoShown.current) {
      preAutoShown.current = true
      if (!status.pre.done) setActiveTest('PRE')
    }
  }, [])

  // สถานะ Pre/Post ของฉัน (ไม่มีข้อสอบ/เรียกไม่ได้ = ไม่แสดงรายการแบบทดสอบ)
  const refreshTests = useCallback(async () => {
    if (!courseId) return
    try {
      applyTestStatus(await getCourseTestsStatus(courseId))
    } catch {
      applyTestStatus(null)
    }
  }, [courseId, applyTestStatus])

  useEffect(() => {
    if (!enrollmentId || !courseId) return
    let cancelled = false
    getCourseTestsStatus(courseId)
      .then((status) => { if (!cancelled) applyTestStatus(status) })
      .catch(() => { if (!cancelled) applyTestStatus(null) })
    return () => { cancelled = true }
  }, [enrollmentId, courseId, applyTestStatus])

  // โหลด enrollment ของคอร์สนี้ + progress ที่เคยบันทึกไว้จาก database มา hydrate completedLessons
  useEffect(() => {
    if (!course) return
    getMyEnrollments()
      .then((list) => {
        const enrollment = list.find((e) => e.course_id === course.course_id)
        if (!enrollment) return
        setEnrollmentId(enrollment.enrollment_id)
        return getEnrollmentProgress(enrollment.enrollment_id)
      })
      .then((progress) => {
        if (!progress) return
        setCompletedLessons(new Set(progress.filter((p) => p.is_completed).map((p) => p.lesson_id)))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.course_id])

  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="mb-4 text-[#69738A]">ไม่พบข้อมูลคอร์ส</p>
          <button onClick={() => navigate('/courses')} className="text-sm text-accent hover:underline">กลับหน้าคอร์สทั้งหมด</button>
        </div>
      </div>
    )
  }

  const allLessons: LessonWithKey[] = course.sections.flatMap((s) =>
    s.lessons.map((l) => ({ ...l, key: l.lesson_id }))
  )
  const currentIndex = allLessons.findIndex((l) => l.key === currentLesson?.key)
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < allLessons.length - 1

  const goPrev = () => { if (hasPrev) setCurrentLesson(allLessons[currentIndex - 1]) }
  // ไปบทถัดไปได้เมื่อบทปัจจุบันเรียนจบแล้วเท่านั้น (บังคับเรียนตามลำดับ) — ยกเว้นเรียกทันทีหลังวิดีโอจบ ซึ่ง markDone ถูกเรียกก่อนในรอบเดียวกัน
  const goNext = () => { if (hasNext) setCurrentLesson(allLessons[currentIndex + 1]) }
  const selectLesson = (lesson: LessonWithKey | null) => { setActiveTest(null); setShowDocs(false); setCurrentLesson(lesson) }
  // ปุ่ม "เริ่มเรียน/เรียนต่อ" ที่หน้าบทนำ — ไปบทแรกที่ยังเรียนไม่จบ (ถ้าจบทุกบทแล้วไปบทแรก)
  const startLearning = () => {
    // มีแบบทดสอบก่อนเรียนที่ยังไม่ได้ทำ/ข้าม → ไปหน้านั้นก่อน (หน้าชี้แจง แล้วค่อยเริ่มทำ) ไม่มี = ไปบทเรียนเลย
    if (testStatus?.pre && !testStatus.pre.done) { setActiveTest('PRE'); return }
    const target = allLessons.find((l) => !completedLessons.has(l.key)) ?? allLessons[0]
    if (target) selectLesson(target)
  }
  const activeTestId = activeTest === 'PRE' ? testStatus?.pre?.test_id : activeTest === 'POST' ? testStatus?.post?.test_id : undefined
  const postTestPending = !!testStatus?.post && !testStatus.post.passed
  // เพิ่งดูบทสุดท้ายจบ: การปลดล็อกยังอยู่ระหว่างบันทึก progress — โหลดสถานะล่าสุดอีกครั้งก่อนเปิด Post-test
  const openPostTest = async () => {
    let status = testStatus
    if (courseId && status?.post && !status.post.unlocked) {
      try {
        status = await getCourseTestsStatus(courseId)
        setTestStatus(status)
      } catch { /* ใช้สถานะเดิม */ }
    }
    if (status?.post?.unlocked && !status.post.passed) setActiveTest('POST')
  }
  const finishTest = () => { setActiveTest(null); refreshTests() }

  const markDone = useCallback((key: number) => {
    setCompletedLessons((prev) => {
      if (prev.has(key)) return prev
      return new Set([...prev, key])
    })
    // side effect นอก setState updater เสมอ (React 18 StrictMode เรียก updater ซ้ำ — ดู knowledge doc section 10)
    if (completedLessons.has(key) || !enrollmentId) return
    const lesson = allLessons.find((l) => l.key === key)
    updateLessonProgress(enrollmentId, key, true, (lesson?.duration_minutes || 0) * 60)
      .then(() => { if (testStatus?.post && !testStatus.post.unlocked) refreshTests() })
      .catch(() => {})
  }, [completedLessons, enrollmentId, allLessons, testStatus, refreshTests])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#F8F7FC]">
      <header className="z-10 flex shrink-0 items-center justify-between border-b border-[#E7E5F0] bg-[#FCFBFF] px-6 py-3">
        <button
          onClick={() => window.history.back()}
          className="flex min-w-0 items-center gap-2 text-sm text-[#69738A] transition-colors hover:text-[#19172A]"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" />
          <span className="shrink-0">กลับไปคอร์สของฉัน</span>
          <span className="shrink-0 text-[#E7E5F0]">/</span>
          <span className="min-w-0 truncate font-medium text-[#19172A]">{course.title}</span>
        </button>
      </header>

      <div className="relative flex flex-1 overflow-hidden">
        <Sidebar
          course={course}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          currentLesson={currentLesson}
          setCurrentLesson={selectLesson}
          completedLessons={completedLessons}
          testStatus={testStatus}
          activeTest={activeTest}
          onSelectTest={(kind) => { setShowDocs(false); setActiveTest(kind) }}
          docsActive={showDocs && !activeTest}
          onSelectDocs={() => { setActiveTest(null); setShowDocs(true) }}
        />
        {showDocs && !activeTest ? (
          <CourseDocuments courseId={course.course_id} />
        ) : activeTest && activeTestId ? (
          <TestRunner key={activeTestId} testId={activeTestId} kind={activeTest} onDone={finishTest} />
        ) : (
        <LessonViewer
          course={course}
          currentLesson={currentLesson}
          completedLessons={completedLessons}
          onMarkDone={markDone}
          onPrev={goPrev}
          onNext={goNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
          allLessons={allLessons}
          currentIndex={currentIndex}
          postTestPending={postTestPending}
          onOpenPostTest={openPostTest}
          onStart={startLearning}
          startLabel={completedLessons.size > 0 ? 'เรียนต่อ' : 'เริ่มเรียน'}
        />
        )}
      </div>
    </div>
  )
}
